import React, { useState, useEffect, useRef } from 'react'
import Api from '../../../../_helpers/Api'
import { trans } from '../../../../_providers/Translation'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import {
    Button,
    Paper,
    Checkbox,
    Grid,
    Box,
    Typography,
    IconButton,
    FormControlLabel
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import ClearIcon from '@mui/icons-material/Clear'
import GetAppIcon from '@mui/icons-material/GetApp'
import DataExportsReady from './DataExportsReady'
import { enqueueSnackbar } from 'notistack'


function DataExport(props) {
    const [loadingDataExport, setLoadingDataExport] = useState(false)

    const dataExportsReadyRef = useRef(null)

    const fetchReadyExports = () => {
        Api.get('/data-exports/ready')
            .then(res => {
                enqueueSnackbar(
                    trans('random.exportReady'),
                    {
                        variant: "success",
                    },
                )

                dataExportsReadyRef.current?.refresh()
            })
            .catch(err => {
                enqueueSnackbar(
                    trans('random.exportFailed'),
                    {
                        variant: "error",
                    },
                )
            })
    }

    const handleDownload = () => {
        const selectedFields = Object.keys(state.exportFields).filter(key => state.exportFields[key])
        setLoadingDataExport(true)

        Api.post('/data-exports/selected-data', {
            fields: selectedFields,
            rows: state.selectedRows
        })
            .then(res => {
                if (res.data && res.data.url) {
                    window.open(res.data.url, '_blank')
                } else {
                    enqueueSnackbar(
                        trans('random.exportStated'),
                        {
                            variant: "success",
                        },
                    )
                }
                fetchReadyExports()
            })
            .catch(err => {
                enqueueSnackbar(
                    trans('random.exportFailed'),
                    {
                        variant: "error",
                    },
                )
                console.error('Export error:', err)
            })
            .finally(() => {
                setLoadingDataExport(false)
            })
    }
    // Build all export field keys from exportGroups
    const allExportFieldKeys = Array.from(new Set([
        ...[
            'customers', 'customers_addresses', 'customers_phone_numbers', 'addresses',
            'contractors', 'contractors_files', 'contractors_types', 'contractors_types_translations', 'contractors_warranties', 'partners', 'partners_employees', 'partners_employees_addresses', 'partners_employees_work_time', 'partners_prices', 'partners_relationships',
            'reservations', 'reservations_comments', 'reservations_images', 'reservations_payments', 'reservations_reports', 'reservations_statuses', 'reservations_statuses_translations', 'reservations_types', 'reservation_appliances', 'reservation_files', 'jobs', 'visit_time',
            'appliances', 'appliances_brands', 'appliances_categories', 'appliances_categories_translations', 'appliances_models', 'appliances_models_translations', 'appliance_history', 'appliance_pictures', 'appliance_servicing_jobs', 'appliance_servicing_actions',
            'products', 'parts', 'parts_translations', 'ordered_parts', 'ordered_parts_data', 'ordered_parts_text_messages', 'used_parts',
            'storages', 'storage', 'storage_locations', 'storage_positions', 'storage_positions_translations', 'storage_statuses', 'storage_statuses_translations', 'storage_ledger', 'storage_history', 'storage_usages', 'storeloads', 'storemoves', 'storeouts', 'vans', 'vans_events',
            'invoices', 'invoices_rows', 'payments', 'expenses', 'expenses_categories', 'sales_orders', 'sales_items', 'banktransactions', 'banktransactions_files', 'banktransactions_payees',
            'messages', 'notifications', 'text_messages', 'text_messages_receivers', 'sent_emails', 'sent_sms_messages', 'voicecalls',
            'website_content', 'website_product', 'website_translation', 'website_reservation_form_fields', 'translations',
            'users', 'permissions', 'permissions_translations', 'user_permissions', 'user_settings', 'notification_settings', 'notification_setting_roles',
            'activitylog', 'reservation_activity_logs', 'loginhistory',
            'contractor_subscriptions', 'subscription_history', 'subscription_types', 'subscription_payment_exceptions',
            'todos', 'todo_alarms', 'todo_tags', 'shared_todos',
            'export_requests'
        ]
    ]))

    const initialExportFields = {}
    allExportFieldKeys.forEach(key => { initialExportFields[key] = false })

    const [state, setState] = useState({
        loading: true,
        data: [],
        filteredData: [],
        searchTerm: '',
        selectedEmployee: '',
        startDate: null,
        endDate: null,
        employees: [],
        page: 0,
        rowsPerPage: 10,
        total: 0,
        selectedRows: [],
        exportFields: initialExportFields
    })
    // Checkbox group definitions (expanded)
    const exportGroups = [
        {
            label: trans('exportGroups.customers'),
            fields: [
                { key: 'customers', label: trans('exportFields.customers') },
                { key: 'customers_addresses', label: trans('exportFields.customers_addresses') },
                { key: 'customers_phone_numbers', label: trans('exportFields.customers_phone_numbers') },
                { key: 'addresses', label: trans('exportFields.addresses') },
            ]
        },
        {
            label: trans('exportGroups.contractors_partners'),
            fields: [
                { key: 'contractors', label: trans('exportFields.contractors') },
                { key: 'contractors_files', label: trans('exportFields.contractors_files') },
                { key: 'contractors_types', label: trans('exportFields.contractors_types') },
                { key: 'contractors_types_translations', label: trans('exportFields.contractors_types_translations') },
                { key: 'contractors_warranties', label: trans('exportFields.contractors_warranties') },
                { key: 'partners', label: trans('exportFields.partners') },
                { key: 'partners_employees', label: trans('exportFields.partners_employees') },
                { key: 'partners_employees_addresses', label: trans('exportFields.partners_employees_addresses') },
                { key: 'partners_employees_work_time', label: trans('exportFields.partners_employees_work_time') },
                { key: 'partners_prices', label: trans('exportFields.partners_prices') },
                { key: 'partners_relationships', label: trans('exportFields.partners_relationships') },
            ]
        },
        {
            label: trans('exportGroups.reservations_jobs'),
            fields: [
                { key: 'reservations', label: trans('exportFields.reservations') },
                { key: 'reservations_comments', label: trans('exportFields.reservations_comments') },
                { key: 'reservations_images', label: trans('exportFields.reservations_images') },
                { key: 'reservations_payments', label: trans('exportFields.reservations_payments') },
                { key: 'reservations_reports', label: trans('exportFields.reservations_reports') },
                { key: 'reservations_statuses', label: trans('exportFields.reservations_statuses') },
                { key: 'reservations_statuses_translations', label: trans('exportFields.reservations_statuses_translations') },
                { key: 'reservations_types', label: trans('exportFields.reservations_types') },
                { key: 'reservation_appliances', label: trans('exportFields.reservation_appliances') },
                { key: 'reservation_files', label: trans('exportFields.reservation_files') },
                { key: 'jobs', label: trans('exportFields.jobs') },
                { key: 'visit_time', label: trans('exportFields.visit_time') },
            ]
        },
        {
            label: trans('exportGroups.appliances'),
            fields: [
                { key: 'appliances', label: trans('exportFields.appliances') },
                { key: 'appliances_brands', label: trans('exportFields.appliances_brands') },
                { key: 'appliances_categories', label: trans('exportFields.appliances_categories') },
                { key: 'appliances_categories_translations', label: trans('exportFields.appliances_categories_translations') },
                { key: 'appliances_models', label: trans('exportFields.appliances_models') },
                { key: 'appliances_models_translations', label: trans('exportFields.appliances_models_translations') },
                { key: 'appliance_history', label: trans('exportFields.appliance_history') },
                { key: 'appliance_pictures', label: trans('exportFields.appliance_pictures') },
                { key: 'appliance_servicing_jobs', label: trans('exportFields.appliance_servicing_jobs') },
                { key: 'appliance_servicing_actions', label: trans('exportFields.appliance_servicing_actions') },
            ]
        },
        {
            label: trans('exportGroups.parts_products'),
            fields: [
                { key: 'products', label: trans('exportFields.products') },
                { key: 'parts', label: trans('exportFields.parts') },
                { key: 'parts_translations', label: trans('exportFields.parts_translations') },
                { key: 'ordered_parts', label: trans('exportFields.ordered_parts') },
                { key: 'ordered_parts_data', label: trans('exportFields.ordered_parts_data') },
                { key: 'ordered_parts_text_messages', label: trans('exportFields.ordered_parts_text_messages') },
                { key: 'used_parts', label: trans('exportFields.used_parts') },
            ]
        },
        {
            label: trans('exportGroups.storage_inventory'),
            fields: [
                { key: 'storages', label: trans('exportFields.storages') },
                { key: 'storage', label: trans('exportFields.storage') },
                { key: 'storage_locations', label: trans('exportFields.storage_locations') },
                { key: 'storage_positions', label: trans('exportFields.storage_positions') },
                { key: 'storage_positions_translations', label: trans('exportFields.storage_positions_translations') },
                { key: 'storage_statuses', label: trans('exportFields.storage_statuses') },
                { key: 'storage_statuses_translations', label: trans('exportFields.storage_statuses_translations') },
                { key: 'storage_ledger', label: trans('exportFields.storage_ledger') },
                { key: 'storage_history', label: trans('exportFields.storage_history') },
                { key: 'storage_usages', label: trans('exportFields.storage_usages') },
                { key: 'storeloads', label: trans('exportFields.storeloads') },
                { key: 'storemoves', label: trans('exportFields.storemoves') },
                { key: 'storeouts', label: trans('exportFields.storeouts') },
                { key: 'vans', label: trans('exportFields.vans') },
                { key: 'vans_events', label: trans('exportFields.vans_events') },
            ]
        },
        {
            label: trans('exportGroups.finance'),
            fields: [
                { key: 'invoices', label: trans('exportFields.invoices') },
                { key: 'invoices_rows', label: trans('exportFields.invoices_rows') },
                { key: 'payments', label: trans('exportFields.payments') },
                { key: 'expenses', label: trans('exportFields.expenses') },
                { key: 'expenses_categories', label: trans('exportFields.expenses_categories') },
                { key: 'sales_orders', label: trans('exportFields.sales_orders') },
                { key: 'sales_items', label: trans('exportFields.sales_items') },
                { key: 'banktransactions', label: trans('exportFields.banktransactions') },
                { key: 'banktransactions_files', label: trans('exportFields.banktransactions_files') },
                { key: 'banktransactions_payees', label: trans('exportFields.banktransactions_payees') },
            ]
        },
        {
            label: trans('exportGroups.communication'),
            fields: [
                { key: 'messages', label: trans('exportFields.messages') },
                { key: 'notifications', label: trans('exportFields.notifications') },
                { key: 'text_messages', label: trans('exportFields.text_messages') },
                { key: 'text_messages_receivers', label: trans('exportFields.text_messages_receivers') },
                { key: 'sent_emails', label: trans('exportFields.sent_emails') },
                { key: 'sent_sms_messages', label: trans('exportFields.sent_sms_messages') },
                { key: 'voicecalls', label: trans('exportFields.voicecalls') },
            ]
        },
        {
            label: trans('exportGroups.website_content'),
            fields: [
                { key: 'website_content', label: trans('exportFields.website_content') },
                { key: 'website_product', label: trans('exportFields.website_product') },
                { key: 'website_translation', label: trans('exportFields.website_translation') },
                { key: 'website_reservation_form_fields', label: trans('exportFields.website_reservation_form_fields') },
                { key: 'translations', label: trans('exportFields.translations') },
            ]
        },
        {
            label: trans('exportGroups.users_access'),
            fields: [
                { key: 'users', label: trans('exportFields.users') },
                { key: 'permissions', label: trans('exportFields.permissions') },
                { key: 'permissions_translations', label: trans('exportFields.permissions_translations') },
                { key: 'user_permissions', label: trans('exportFields.user_permissions') },
                { key: 'user_settings', label: trans('exportFields.user_settings') },
                { key: 'notification_settings', label: trans('exportFields.notification_settings') },
                { key: 'notification_setting_roles', label: trans('exportFields.notification_setting_roles') },
            ]
        },
        {
            label: trans('exportGroups.activity_history'),
            fields: [
                { key: 'activitylog', label: trans('exportFields.activitylog') },
                { key: 'reservation_activity_logs', label: trans('exportFields.reservation_activity_logs') },
                { key: 'loginhistory', label: trans('exportFields.loginhistory') },
            ]
        },
        {
            label: trans('exportGroups.subscriptions'),
            fields: [
                { key: 'contractor_subscriptions', label: trans('exportFields.contractor_subscriptions') },
                { key: 'subscription_history', label: trans('exportFields.subscription_history') },
                { key: 'subscription_types', label: trans('exportFields.subscription_types') },
                { key: 'subscription_payment_exceptions', label: trans('exportFields.subscription_payment_exceptions') },
            ]
        },
        {
            label: trans('exportGroups.tasks_todos'),
            fields: [
                { key: 'todos', label: trans('exportFields.todos') },
                { key: 'todo_alarms', label: trans('exportFields.todo_alarms') },
                { key: 'todo_tags', label: trans('exportFields.todo_tags') },
                { key: 'shared_todos', label: trans('exportFields.shared_todos') },
            ]
        },
        {
            label: trans('exportGroups.exports'),
            fields: [
                { key: 'export_requests', label: trans('exportFields.export_requests') }
            ]
        }
    ]

    const handleExportFieldChange = (key) => {
        setState(prev => ({
            ...prev,
            exportFields: {
                ...prev.exportFields,
                [key]: !prev.exportFields[key]
            }
        }))
    }

    const allSelected = Object.values(state.exportFields).every(v => v === true)
    const selectAllLabel = allSelected ? (trans('labels.deselectAll') || 'Deselect All') : (trans('labels.selectAll') || 'Select All')
    return (
        <div className='pannel'>
            <div className='settings-data-export'>
                <div className="head">
                    <h3>
                        {trans('tabs.dataExport')}
                    </h3>
                </div>
                <hr />

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={allSelected}
                            indeterminate={Object.values(state.exportFields).some(v => v) && !allSelected}
                            onChange={() => {
                                const newExportFields = {}
                                Object.keys(state.exportFields).forEach(key => {
                                    newExportFields[key] = !allSelected
                                })
                                setState(prev => ({
                                    ...prev,
                                    exportFields: newExportFields
                                }))
                            }}
                            color="primary"
                        />
                    }
                    label={selectAllLabel}
                    sx={{
                        marginBottom: "5px",
                    }}
                />
                <Grid container spacing={2}>
                    {[...exportGroups].sort((a, b) => b.fields.length - a.fields.length).map((group, gidx) => (
                        <Grid item xs={12} sm={6} md={4} key={gidx}>
                            <table>
                                <thead>
                                    <tr>
                                        <th>
                                            {group.label}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.fields.map(field => (
                                        <tr key={field.key}>
                                            <td>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            color="primary"
                                                            checked={!!state.exportFields[field.key]}
                                                            onChange={() => handleExportFieldChange(field.key)}
                                                            sx={{
                                                                marginTop: "-9px",
                                                                marginBottom: "-9px"
                                                            }}
                                                        />
                                                    }
                                                    label={field.label}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Grid>
                    ))}
                </Grid>
                <hr />

                <DataExportsReady fetchReadyExports={fetchReadyExports} handleDownload={handleDownload} loadingDataExport={loadingDataExport} ref={dataExportsReadyRef} />
            </div>
        </div>
    )
}

export default DataExport